import { useEffect, useState } from "react";
import { Menu, X, Globe, BookOpen, Sun, Moon } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { whatsappLink, scrollToId } from "../config/site";
import { useLang } from "../config/i18n";

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { lang, setLang, t } = useLang();
  const { pathname } = useLocation();
  const isGoetia = pathname.startsWith("/goetia");
  const [theme, setTheme] = useState(() => localStorage.getItem("mjb-theme") || "dark");

  useEffect(() => {
    document.documentElement.classList.toggle("light", theme === "light");
    localStorage.setItem("mjb-theme", theme);
  }, [theme]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    if (isGoetia) window.location.href = `/#${id}`;
    else scrollToId(id);
  };

  const ThemeToggle = ({ className = "" }) => (
    <button
      data-testid="nav-theme-toggle"
      onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
      className={`inline-flex items-center justify-center w-10 h-10 rounded-sm border border-[#22222B] text-[#A3A3AF] hover:border-[#D9A94A] hover:text-[#D9A94A] transition-colors duration-300 ${className}`}
      aria-label={theme === "dark" ? "Modo claro" : "Modo escuro"}
    >
      {theme === "dark" ? <Sun size={14} strokeWidth={1.6} /> : <Moon size={14} strokeWidth={1.6} />}
    </button>
  );

  const LangToggle = ({ className = "" }) => (
    <button
      data-testid="nav-lang-toggle"
      onClick={() => setLang(lang === "pt" ? "en" : "pt")}
      className={`inline-flex items-center justify-center gap-1.5 min-h-[40px] px-3 rounded-sm border border-[#22222B] text-[10px] font-semibold tracking-[0.15em] uppercase text-[#A3A3AF] hover:border-[#D9A94A] hover:text-[#D9A94A] transition-colors duration-300 ${className}`}
      aria-label="Mudar idioma / Switch language"
    >
      <Globe size={13} strokeWidth={1.6} /> {lang === "pt" ? "EN" : "PT"}
    </button>
  );

  return (
    <header
      data-testid="nav-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 border-b ${
        scrolled
          ? "bg-[#0A0A0C]/90 backdrop-blur-xl border-[#C5A059]/20"
          : "bg-transparent border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => go("inicio")}
          className="flex items-center gap-3 group"
        >
          <img
            src="/selo-mestre-jonas.png"
            alt="Selo Mestre Jonas Barcellos"
            className="w-10 h-10 rounded-full object-cover border border-[#C5A059]/40 transition-transform duration-700 group-hover:scale-105"
          />
          <span className="font-heading text-xs sm:text-sm tracking-[0.18em] text-[#F7F7F8]">
            MESTRE JONAS <span className="text-[#C5A059]">BARCELLOS</span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-5" data-testid="nav-desktop-links">
          {t.navLinks.map(([label, id]) => (
            <button
              key={id}
              data-testid={`nav-link-${id}`}
              onClick={() => go(id)}
              className="text-[10px] tracking-[0.16em] uppercase text-[#A3A3AF] hover:text-[#D9A94A] transition-colors duration-300"
            >
              {label}
            </button>
          ))}
          <Link
            data-testid="nav-link-goetia"
            to="/goetia"
            className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.16em] uppercase text-[#C5A059] hover:text-[#E9BE62] transition-colors duration-300"
          >
            <BookOpen size={12} /> Goétia
          </Link>
        </nav>

        <div className="flex items-center gap-3">
          <ThemeToggle className="hidden sm:inline-flex" />
          <LangToggle className="hidden sm:inline-flex" />
          <a
            data-testid="nav-whatsapp-btn"
            href={whatsappLink(t.waDefault)}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-gold nav-cta hidden sm:inline-flex min-h-[44px] px-5"
          >
            {t.navCta}
          </a>
          <button
            data-testid="nav-mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden w-11 h-11 flex items-center justify-center text-[#F7F7F8]"
            aria-label="Abrir menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="nav-mobile-menu" className="lg:hidden bg-[#0A0A0C]/97 backdrop-blur-xl border-t border-[#C5A059]/15 px-6 py-6">
          <div className="flex flex-col gap-1">
            {t.navLinks.map(([label, id]) => (
              <button
                key={id}
                data-testid={`nav-mobile-link-${id}`}
                onClick={() => go(id)}
                className="text-left py-3.5 min-h-[52px] text-sm tracking-[0.15em] uppercase text-[#D1D1DB] border-b border-[#22222B] hover:text-[#C5A059] transition-colors"
              >
                {label}
              </button>
            ))}
            <Link
              data-testid="nav-mobile-link-goetia"
              to="/goetia"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 py-3.5 min-h-[52px] text-sm tracking-[0.15em] uppercase text-[#C5A059] border-b border-[#22222B]"
            >
              <BookOpen size={15} /> Enciclopédia da Goétia
            </Link>
            <div className="mt-5 flex gap-3">
              <ThemeToggle className="flex-1 min-h-[48px]" />
              <LangToggle className="flex-1 min-h-[48px]" />
            </div>
            <a
              data-testid="nav-mobile-whatsapp-btn"
              href={whatsappLink(t.waDefault)}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 btn-gold w-full"
            >
              {t.navCta}
            </a>
          </div>
        </div>
      )}
    </header>
  );
};
